import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format, endOfMonth, startOfMonth, subMonths } from "date-fns";
import toast from "react-hot-toast";
import { Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { isCreditNote } from "@fiscalo/shared-types";
import { useAuth } from "@/contexts/AuthContext";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { LoadingPage } from "@/components/shared/LoadingPage";
import { PageHeader } from "@/components/shared/PageHeader";
import { getEstadoFiscalFinancial, getIvaHistory } from "@/services/dashboardService";
import {
  listInvoices,
  listRecibidas,
  type ListInvoicesResponse,
  type RecibidaItem,
} from "@/services/invoiceService";
import {
  getFiscalAgenda,
  getFiscalProfile,
  getFiscalHealth,
  getAutoBoletoStatuses,
  markFiscalObligationCompleted,
  recordFiscalObligationPayment,
  removeBoleto,
  uploadBoleto,
  type FiscalAgendaObligation,
  type AutoBoletoStatus,
} from "@/services/fiscalService";
import { AutoBoletoStatusChip } from "@/components/shared/AutoBoletoStatusChip";
import {
  listTenantMembers,
  listTenantPendingInvites,
  type TenantPendingInvite,
} from "@/services/tenantUsersService";
import { estadoFiscalKeys, tenantKeys, tenantMemberKeys } from "@/lib/queryKeys";
import { getTenantCurrent, uploadTenantDocument } from "@/services/authService";
import { formatDashboardCurrency } from "@/lib/currencyFormat";
import { formatDisplayDate, parseLocalDateOnly } from "@/lib/dateFormat";
import { cn } from "@/lib/utils";
import { useEffectiveHealthBreakdown } from "@/hooks/useEffectiveHealthBreakdown";
import { useUpcomingObligationsTimeline } from "@/hooks/useUpcomingObligationsTimeline";
import {
  type HealthBand,
  type HealthScoreBreakdown,
} from "@/pages/dashboard/fiscalHealthScore";
import { FiscalHealthExplanationModal } from "@/pages/dashboard/FiscalHealthExplanationModal";
import { EstadoFiscalAccountantsCard } from "@/pages/estado-fiscal/EstadoFiscalAccountantsCard";
import {
  SaludFiscalHero,
  FacturacionCard,
  ContadorCard,
} from "@/pages/estado-fiscal/SaludFiscalHero";
import {
  ObligationCard,
  BalanceEquation,
  ObligationDetailFoot,
  ObligacionesSectionHead,
  type ObligationState,
} from "@/pages/estado-fiscal/ObligationCard";
import {
  Proximos60Dias,
  MiniCalendar,
  type MiniCalendarKind,
} from "@/pages/estado-fiscal/Proximos60Dias";
import { MarkFiscalCompleteDialog } from "@/components/shared/MarkFiscalCompleteDialog";
import { UploadBoletoForm } from "@/components/shared/UploadBoletoForm";
import { isFreePlan, isUltraPlan } from "@/components/shared/PaidPlanRoute";
import { ConfirmationDialog } from "@/components/shared/ConfirmationDialog";
import { TableCard } from "@/components/shared/TableCard";
import { EmptyState } from "@/components/shared/EmptyState";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  getTaxLabelKey,
  formatPeriodForDisplay,
} from "@/lib/fiscalAgendaDisplay";
import { flattenAgenda } from "@/pages/dashboard/dashboardAlertsModel";

/** DGI rounding: decimal < 0.05 round down, ≥ 0.05 round up (e.g. 1.04 → 1, 1.05 → 2). */
function roundDgi(value: number): number {
  const abs = Math.abs(value);
  const sign = value < 0 ? -1 : 1;
  const floor = Math.floor(abs);
  const frac = abs - floor;
  const rounded = frac < 0.05 ? floor : Math.ceil(abs);
  return sign * rounded;
}

function scoreToBand(score: number): HealthBand {
  if (score >= 80) return "healthy";
  if (score >= 50) return "needsAttention";
  return "atRisk";
}

const ISO_DATE = "yyyy-MM-dd";

const DGI_2908_PAYMENT_URL =
  "https://servicios.dgi.gub.uy/serviciosenlinea/dgi--servicios-en-linea--boleto-de-pago-2908--generacion-impresion-y-pago";
const DGI_2908_PAY_BY_RUT_URL =
  "https://servicios.dgi.gub.uy/serviciosenlinea/dgi--servicios-en-linea--boleto-de-pago-2908-por-rut-y-numero-de-boleto--impresion-y-pago";

export function EstadoFiscalPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [healthModalOpen, setHealthModalOpen] = useState(false);
  const [ivaDetailExpanded, setIvaDetailExpanded] = useState(false);
  const [ivaMarkPaidDialogObligation, setIvaMarkPaidDialogObligation] =
    useState<FiscalAgendaObligation | null>(null);
  const [ivaInstructionsOpen, setIvaInstructionsOpen] = useState(false);
  const [payBoletoModalObligation, setPayBoletoModalObligation] = useState<FiscalAgendaObligation | null>(null);
  const [uploadBoletoDialogOpen, setUploadBoletoDialogOpen] = useState(false);
  const [removeBoletoObligation, setRemoveBoletoObligation] =
    useState<FiscalAgendaObligation | null>(null);
  const [agendaSlotMarkCompleteObligation, setAgendaSlotMarkCompleteObligation] =
    useState<FiscalAgendaObligation | null>(null);
  /** Open record-payment dialog for a specific IVA history row (period + obligationId). */
  const [recordIvaPaymentRow, setRecordIvaPaymentRow] = useState<{
    period: string;
    obligationId: string;
    currentAmount: number | null;
  } | null>(null);
  const [recordIvaPaymentInput, setRecordIvaPaymentInput] = useState("");

  const isAccountant = user?.role === "accountant";

  const { data: currentTenant, isLoading: tenantLoading } = useQuery({
    queryKey: tenantKeys.current(user?.tenantId ?? undefined),
    queryFn: getTenantCurrent,
    enabled: Boolean(user?.tenantId),
  });
  const hasPaidPlanAccess = !isFreePlan(currentTenant?.planId);
  const isUltraTenant = isUltraPlan(currentTenant?.planId);

  const {
    data: estadoFiscalFinancial,
    isLoading: financialLoading,
    isError: financialError,
  } = useQuery({
    queryKey: estadoFiscalKeys.financial(),
    queryFn: getEstadoFiscalFinancial,
    enabled: hasPaidPlanAccess,
  });

  const year = new Date().getFullYear();
  const now = new Date();
  const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const lastMonthName = new Intl.DateTimeFormat(i18n.language, {
    month: "long",
    year: "numeric",
  }).format(lastMonthDate);
  const prevMonthStart = format(
    startOfMonth(subMonths(now, 1)),
    ISO_DATE
  );
  const prevMonthEnd = format(
    endOfMonth(subMonths(now, 1)),
    ISO_DATE
  );
  const queryClient = useQueryClient();
  const agendaFrom = `${year - 1}-01`;
  const agendaTo = `${year}-12`;
  const { data: agendaGrouped } = useQuery({
    queryKey: ["fiscal-agenda", agendaFrom, agendaTo],
    queryFn: () => getFiscalAgenda(agendaFrom, agendaTo),
    enabled: hasPaidPlanAccess,
  });

  const { data: ivaHistory = [], isLoading: ivaHistoryLoading } = useQuery({
    queryKey: ["estado-fiscal", "iva-history", agendaFrom, agendaTo],
    queryFn: () => getIvaHistory(agendaFrom, agendaTo),
    enabled: hasPaidPlanAccess,
  });

  const { data: fiscalProfile } = useQuery({
    queryKey: ["fiscal-profile"] as const,
    queryFn: getFiscalProfile,
    retry: false,
    enabled: hasPaidPlanAccess,
  });

  const { data: fiscalHealth, isError: fiscalHealthError } = useQuery({
    queryKey: ["fiscal-health"],
    queryFn: getFiscalHealth,
    enabled: hasPaidPlanAccess,
  });

  const tenantId = currentTenant?.id ?? "";
  const { data: tenantMembers = [] } = useQuery({
    queryKey: tenantMemberKeys.list(tenantId),
    queryFn: () => listTenantMembers(tenantId),
    enabled: hasPaidPlanAccess && Boolean(tenantId) && !isAccountant,
  });
  const { data: pendingInvites = [] } = useQuery({
    queryKey: tenantMemberKeys.pendingInvites(tenantId),
    queryFn: () => listTenantPendingInvites(tenantId),
    enabled: hasPaidPlanAccess && Boolean(tenantId) && !isAccountant,
  });
  const accountants = useMemo(
    () => tenantMembers.filter((m) => m.role === "accountant"),
    [tenantMembers]
  );
  const pendingAccountantInvites = useMemo(
    () => (pendingInvites as TenantPendingInvite[]).filter((inv) => inv.role === "accountant"),
    [pendingInvites]
  );

  const { data: emitidasData, isLoading: emitidasLoading } = useQuery({
    queryKey: ["estado-fiscal", "iva-detail", "emitidas", prevMonthStart, prevMonthEnd],
    queryFn: () =>
      listInvoices({
        period: "custom",
        dateFrom: prevMonthStart,
        dateTo: prevMonthEnd,
        status: "accepted",
        limit: 100,
      }),
    enabled: hasPaidPlanAccess && ivaDetailExpanded,
  });

  const { data: recibidasData, isLoading: recibidasLoading } = useQuery({
    queryKey: ["estado-fiscal", "iva-detail", "recibidas", prevMonthStart, prevMonthEnd],
    queryFn: () =>
      listRecibidas({
        dateFrom: prevMonthStart,
        dateTo: prevMonthEnd,
        limit: 100,
        forIvaCompras: true,
      }),
    enabled: hasPaidPlanAccess && ivaDetailExpanded,
  });

  /**
   * Table of the user's own emitidas (CFE 101/111/121) that contributed to
   * IVA débito for the period. Mirrors the recibidas helper below so both
   * lists feel like one detail surface.
   */
  function renderIvaVentasEmitidasTable(
    emitidas: ListInvoicesResponse["data"],
  ): React.ReactNode {
    if (emitidas.length === 0) {
      return (
        <p className="py-3 text-sm text-muted-foreground">
          {t("estadoFiscal.ivaDetailNoEmitidas")}
        </p>
      );
    }
    return (
      <div className="overflow-x-auto rounded-md border border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow className="border-b border-border bg-muted/70 hover:bg-muted/70">
              <TableHead className="font-semibold text-foreground">
                {t("estadoFiscal.ivaDetailDate")}
              </TableHead>
              <TableHead className="font-semibold text-foreground">
                {t("estadoFiscal.ivaDetailDoc")}
              </TableHead>
              <TableHead className="text-right font-semibold text-foreground">
                {t("estadoFiscal.ivaDetailTotal")}
              </TableHead>
              <TableHead className="text-right font-semibold text-foreground">
                {t("estadoFiscal.ivaDetailIva")}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {emitidas.map((inv) => {
              const total = inv.totals?.total?.amount ?? 0;
              const iva = inv.totals?.taxTotal?.amount ?? 0;
              const cur = (
                inv.totals?.total?.currency ??
                inv.currency ??
                "UYU"
              ).toUpperCase();
              const docLabel =
                inv.cfeType === "101"
                  ? t("estadoFiscal.ivaDetailETicket", "e-Ticket")
                  : inv.cfeType === "111"
                    ? t("estadoFiscal.ivaDetailEFactura", "e-Factura")
                    : inv.cfeType === "121"
                      ? t(
                          "estadoFiscal.ivaDetailEExport",
                          "e-Factura Exportación",
                        )
                      : inv.cfeType;
              return (
                <TableRow
                  key={inv.id}
                  className="cursor-pointer border-b border-border transition-colors hover:bg-muted/50"
                  onClick={() => navigate(`/invoices/${inv.id}`)}
                >
                  <TableCell className="text-foreground">
                    {formatDisplayDate(inv.issueDate ?? "")}
                  </TableCell>
                  <TableCell className="text-foreground">
                    {docLabel}{" "}
                    {inv.internalNumber ?? inv.id.slice(0, 8)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-foreground">
                    {formatDashboardCurrency(total)} {cur}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-foreground">
                    {formatDashboardCurrency(iva)} {cur}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    );
  }

  function renderIvaComprasRecibidasTable(recibidas: RecibidaItem[]): React.ReactNode {
    if (recibidas.length === 0) {
      return (
        <p className="py-3 text-sm text-muted-foreground">
          {t("estadoFiscal.ivaDetailNoRecibidas")}
        </p>
      );
    }
    return (
      <div className="overflow-x-auto rounded-md border border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow className="border-b border-border bg-muted/70 hover:bg-muted/70">
              <TableHead className="font-semibold text-foreground">
                {t("estadoFiscal.ivaDetailDate")}
              </TableHead>
              <TableHead className="font-semibold text-foreground">
                {t("estadoFiscal.ivaDetailEmitter")}
              </TableHead>
              <TableHead className="font-semibold text-foreground">
                {t("estadoFiscal.ivaDetailDoc")}
              </TableHead>
              <TableHead className="text-right font-semibold text-foreground">
                {t("estadoFiscal.ivaDetailTotal")}
              </TableHead>
              <TableHead className="text-right font-semibold text-foreground">
                {t("estadoFiscal.ivaDetailIva")}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {recibidas.map((r) => {
              const displayTotal =
                r.tipoCFE === "182" && Number(r.montoTotal) === 0 && Number(r.totalNeto) > 0
                  ? Number(r.totalNeto)
                  : Number(r.montoTotal);
              return (
                <TableRow key={r.id} className="border-b border-border transition-colors hover:bg-muted/50">
                  <TableCell className="text-foreground">
                    {formatDisplayDate(r.fechaHora?.slice(0, 10) ?? "")}
                  </TableCell>
                  <TableCell className="text-foreground">
                    <div className="flex flex-col">
                      <span className="font-medium">
                        {r.legalName?.trim() || r.rucEmisor?.trim() || "—"}
                      </span>
                      {r.legalName?.trim() && r.rucEmisor?.trim() && (
                        <span className="text-xs text-foreground/80 tabular-nums">
                          {r.rucEmisor.trim()}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-foreground">
                    {t(`invoices.cfeTypes.${r.tipoCFE}`, r.tipoCFE)} / {t("recibidas.serieLabel", "Serie")} {r.serie} {r.nro}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-foreground">
                    {formatDashboardCurrency(isCreditNote(r.tipoCFE) ? -displayTotal : displayTotal)} {r.moneda}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-foreground">
                    {formatDashboardCurrency(
                      isCreditNote(r.tipoCFE) ? -Number(r.totalIVA) : Number(r.totalIVA)
                    )}{" "}
                    {r.moneda}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    );
  }

  const activeMonthKey = format(now, "yyyy-MM");
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();
  const activeMonthObligations = useMemo((): FiscalAgendaObligation[] => {
    const byKey = agendaGrouped?.[activeMonthKey] ?? [];
    if (byKey.length > 0) {
      return [...byKey].sort(
        (a, b) =>
          parseLocalDateOnly(a.dueDate).getTime() -
          parseLocalDateOnly(b.dueDate).getTime()
      );
    }
    if (!agendaGrouped) return [];
    const all = flattenAgenda(agendaGrouped);
    const inCurrentMonth = all.filter((o) => {
      const due = parseLocalDateOnly(o.dueDate);
      return due.getFullYear() === currentYear && due.getMonth() === currentMonth;
    });
    return inCurrentMonth.sort(
      (a, b) =>
        parseLocalDateOnly(a.dueDate).getTime() -
        parseLocalDateOnly(b.dueDate).getTime()
    );
  }, [agendaGrouped, activeMonthKey, currentYear, currentMonth]);

  const ivaDgiObligationIdsThisMonth = useMemo((): string[] => {
    return activeMonthObligations
      .filter((o) => o.taxType === "IVA" && o.authority === "DGI")
      .map((o) => o.id);
  }, [activeMonthObligations]);

  const { data: autoBoletoStatuses } = useQuery({
    queryKey: ["auto-boleto-status", ivaDgiObligationIdsThisMonth.join(",")],
    queryFn: () => getAutoBoletoStatuses(ivaDgiObligationIdsThisMonth),
    enabled: ivaDgiObligationIdsThisMonth.length > 0,
    refetchInterval: (query): number | false => {
      // Poll while a flow is actively running so the chip transitions success/failed in real time.
      const data = query.state.data as Record<string, AutoBoletoStatus> | undefined;
      if (!data) return false;
      return Object.values(data).some((s) => s.state === "in_progress") ? 15000 : false;
    },
    staleTime: 60_000,
  });

  const ivaObligationForPeriod = useMemo((): FiscalAgendaObligation | null => {
    if (!agendaGrouped) return null;
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    const list = flattenAgenda(agendaGrouped);
    return (
      list.find((o) => {
        if (o.taxType !== "IVA" || o.authority !== "DGI") return false;
        const due = parseLocalDateOnly(o.dueDate);
        return (
          due.getFullYear() === currentYear && due.getMonth() === currentMonth
        );
      }) ?? null
    );
  }, [agendaGrouped]);

  const currentMonth1Based = now.getMonth() + 1;
  const ivaHistoryCurrentYearOnly = useMemo(() => {
    const currentYearStr = String(currentYear);
    const filtered = ivaHistory.filter((row) => {
      const [y, m] = row.period.split("-");
      const monthNum = parseInt(m ?? "0", 10);
      if (y !== currentYearStr || monthNum < 1) return false;
      const dueMonthForPeriod = monthNum + 1;
      const isPast = dueMonthForPeriod < currentMonth1Based;
      return isPast;
    });
    return [...filtered].sort((a, b) => b.period.localeCompare(a.period));
  }, [ivaHistory, currentYear, currentMonth1Based]);

  const markIvaPaid = useMutation({
    mutationFn: (args: {
      obligationId: string;
      completedDate: string;
      notes?: string;
      amountPaidUyu?: number | null;
    }) =>
      markFiscalObligationCompleted(args.obligationId, {
        completedDate: args.completedDate,
        notes: args.notes,
        amountPaidUyu: args.amountPaidUyu,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fiscal-agenda"] });
      queryClient.invalidateQueries({ queryKey: ["fiscal-health"] });
      queryClient.invalidateQueries({ queryKey: estadoFiscalKeys.financial() });
      queryClient.invalidateQueries({ queryKey: estadoFiscalKeys.history() });
      toast.success(t("estadoFiscal.ivaMarkedAsPaid"));
      setIvaMarkPaidDialogObligation(null);
    },
    onError: () => {
      toast.error(t("settings.fiscalProfile.error"));
    },
  });

  const recordIvaPayment = useMutation({
    mutationFn: (args: { obligationId: string; amountPaidUyu: number | null }) =>
      recordFiscalObligationPayment(args.obligationId, args.amountPaidUyu),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: estadoFiscalKeys.financial() });
      queryClient.invalidateQueries({ queryKey: estadoFiscalKeys.history() });
      queryClient.invalidateQueries({ queryKey: ["fiscal-agenda"] });
      toast.success(t("estadoFiscal.ivaPaymentRecorded"));
      setRecordIvaPaymentRow(null);
      setRecordIvaPaymentInput("");
    },
    onError: () => {
      toast.error(t("settings.fiscalProfile.error"));
    },
  });

  const markAgendaSlotComplete = useMutation({
    mutationFn: (args: {
      obligationId: string;
      completedDate: string;
      notes?: string;
    }) =>
      markFiscalObligationCompleted(args.obligationId, {
        completedDate: args.completedDate,
        notes: args.notes,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fiscal-agenda"] });
      queryClient.invalidateQueries({ queryKey: ["fiscal-health"] });
      toast.success(t("estadoFiscal.ivaMarkedAsPaid"));
      setAgendaSlotMarkCompleteObligation(null);
    },
    onError: () => {
      toast.error(t("settings.fiscalProfile.error"));
    },
  });

  const uploadBoletoMutation = useMutation({
    mutationFn: async (args: {
      obligationId: string;
      boletoNumber: string;
      file: File;
    }) => {
      const doc = await uploadTenantDocument(args.file);
      return uploadBoleto(args.obligationId, {
        boletoNumber: args.boletoNumber,
        boletoFileUrl: doc.fileUrl,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fiscal-agenda"] });
      toast.success(t("estadoFiscal.boletoUploaded"));
      setUploadBoletoDialogOpen(false);
    },
    onError: () => {
      toast.error(t("settings.fiscalProfile.error"));
    },
  });

  const removeBoletoMutation = useMutation({
    mutationFn: (obligationId: string) => removeBoleto(obligationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fiscal-agenda"] });
      setRemoveBoletoObligation(null);
      toast.success(t("estadoFiscal.boletoRemoved"));
    },
    onError: () => {
      toast.error(t("settings.fiscalProfile.error"));
    },
  });

  useEffect(() => {
    if (hasPaidPlanAccess && (fiscalHealthError || financialError)) {
      toast.error(t("estadoFiscal.errorLoad"));
    }
  }, [hasPaidPlanAccess, fiscalHealthError, financialError, t]);

  const healthBreakdown = useMemo<HealthScoreBreakdown>(() => {
    if (fiscalHealth) {
      return {
        score: fiscalHealth.score,
        band: scoreToBand(fiscalHealth.score),
        penalties: {
          overdue: fiscalHealth.factors.overdueCount,
          dueIn7Days: fiscalHealth.factors.dueIn3Count,
          noCae: 0,
          caeLow: 0,
          noInvoicesThisMonth: 0,
        },
        fiscalApiFactors: fiscalHealth.factors,
      };
    }
    return {
      score: 0,
      band: "atRisk" as HealthBand,
      penalties: {
        overdue: 0,
        dueIn7Days: 0,
        noCae: 0,
        caeLow: 0,
        noInvoicesThisMonth: 0,
      },
    };
  }, [fiscalHealth]);

  const overdueObligations = useMemo((): FiscalAgendaObligation[] => {
    if (!agendaGrouped) return [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return flattenAgenda(agendaGrouped).filter((o) => {
      if (o.authority !== "DGI" || o.status === "completed") return false;
      const due = parseLocalDateOnly(o.dueDate);
      return due < today;
    });
  }, [agendaGrouped]);

  const dueTodayObligations = useMemo((): FiscalAgendaObligation[] => {
    if (!agendaGrouped) return [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return flattenAgenda(agendaGrouped).filter((o) => {
      if (o.authority !== "DGI" || o.status === "completed") return false;
      const due = parseLocalDateOnly(o.dueDate);
      return due.getTime() === today.getTime();
    });
  }, [agendaGrouped]);

  const dueIn1Obligations = useMemo((): FiscalAgendaObligation[] => {
    if (!agendaGrouped) return [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return flattenAgenda(agendaGrouped).filter((o) => {
      if (o.authority !== "DGI" || o.status === "completed") return false;
      const due = parseLocalDateOnly(o.dueDate);
      return due.getTime() === tomorrow.getTime();
    });
  }, [agendaGrouped]);

  const dueIn3Obligations = useMemo((): FiscalAgendaObligation[] => {
    if (!agendaGrouped) return [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const in3 = new Date(today);
    in3.setDate(in3.getDate() + 3);
    return flattenAgenda(agendaGrouped).filter((o) => {
      if (o.authority !== "DGI" || o.status === "completed") return false;
      const due = parseLocalDateOnly(o.dueDate);
      return due > tomorrow && due <= in3;
    });
  }, [agendaGrouped]);

  const effectiveBreakdown = useEffectiveHealthBreakdown(
    healthBreakdown,
    overdueObligations,
    dueTodayObligations,
    dueIn1Obligations,
    dueIn3Obligations
  );

  // Pre-compute the DGI total before any early returns so the timeline hook below
  // (which itself calls hooks) is always invoked in the same order. Safe-default to
  // 0 when financial data isn't loaded yet.
  const ivaDgiTotalForHook = estadoFiscalFinancial
    ? (() => {
        const showSaldo = estadoFiscalFinancial.ivaCreditBalanceUyu > 0;
        const raw = showSaldo
          ? estadoFiscalFinancial.ivaAPagarNetoUyu
          : estadoFiscalFinancial.ivaAPagarEstimadoUyu;
        return roundDgi(raw);
      })()
    : 0;

  // Shared hook: derives the next-60-days timeline (also used on the dashboard).
  // Must be called before any conditional early returns to satisfy Rules of Hooks.
  const timelineEvents = useUpcomingObligationsTimeline({
    agendaGrouped,
    ivaDgiTotal: ivaDgiTotalForHook,
    ivaObligationId: ivaObligationForPeriod?.id ?? null,
    variant: "rich",
  });

  if (tenantLoading) {
    return <LoadingPage />;
  }

  if (!hasPaidPlanAccess) {
    return (
      <div className="space-y-4 fx-content">
        <PageHeader
          eyebrow={t("estadoFiscal.eyebrow", "Contabilidad")}
          title={
            <>
              Estado <span className="fx-h1-serif">fiscal.</span>
            </>
          }
          description={t("estadoFiscal.subtitle")}
        />
        <div
          className="min-h-[32vh] rounded-lg border border-dashed border-[var(--fx-ink-200)] bg-[var(--fx-paper-2)] dark:border-[var(--dk-border-2)] dark:bg-[var(--dk-surface)]"
          aria-hidden
        />
      </div>
    );
  }

  if (financialLoading || !estadoFiscalFinancial) {
    return <LoadingPage />;
  }

  const neto = estadoFiscalFinancial.totalNetoThisMonthUyu;
  const iva = estadoFiscalFinancial.totalIvaThisMonthUyu;
  const ivaDebited = estadoFiscalFinancial.ivaDebitedPrevMonthUyu;
  const ivaCredited = estadoFiscalFinancial.ivaCreditedPrevMonthUyu;
  const ivaEstimado = estadoFiscalFinancial.ivaAPagarEstimadoUyu;
  const ivaCreditBalance = estadoFiscalFinancial.ivaCreditBalanceUyu;
  const ivaNeto = estadoFiscalFinancial.ivaAPagarNetoUyu;
  // Show carry-over only when it actually reduces what the user has to pay — i.e., a positive
  // credit balance from prior overpayments. Negative balances (under-paid) are informational and
  // intentionally not surfaced here to avoid alarm.
  const showSaldoAFavor = ivaCreditBalance > 0;
  const ivaDgiTotal = roundDgi(showSaldoAFavor ? ivaNeto : ivaEstimado);

  const ivaRegime = fiscalProfile?.ivaRegime;
  const ivaRegimeFullLabelKey =
    ivaRegime === "no_cede"
      ? "settings.fiscalProfile.ivaRegimeNoCede"
      : ivaRegime === "cede"
        ? "settings.fiscalProfile.ivaRegimeCede"
        : ivaRegime === "minimum"
          ? "settings.fiscalProfile.ivaRegimeMinimum"
          : null;
  const ivaRegimeShortLabelKey =
    ivaRegime === "no_cede"
      ? "estadoFiscal.ivaRegimeShortNoCede"
      : ivaRegime === "cede"
        ? "estadoFiscal.ivaRegimeShortCede"
        : ivaRegime === "minimum"
          ? "estadoFiscal.ivaRegimeShortMinimum"
          : null;

  // ─── Derived data for new estado-fiscal layout ────────────────────────────
  // Format as "Mayo 2026" — month name capitalized + year, no locale "de" connector
  // so the eyebrow doesn't render as "MAYO DE 2026" once text-transform: uppercase applies.
  const monthOnlyFmt = new Intl.DateTimeFormat(i18n.language, { month: "long" });
  const monthName = monthOnlyFmt.format(now);
  const heroPeriodLabel = `${monthName.charAt(0).toUpperCase()}${monthName.slice(1)} ${now.getFullYear()}`;
  const issuesCount =
    effectiveBreakdown.penalties.overdue +
    effectiveBreakdown.penalties.dueIn7Days;
  const isHealthy = effectiveBreakdown.band === "healthy";
  const heroStatusLead = isHealthy
    ? t("estadoFiscal.heroStatusOkLead", "Sin problemas")
    : effectiveBreakdown.band === "needsAttention"
      ? t("estadoFiscal.heroStatusWarnLead", "Algunos puntos")
      : t("estadoFiscal.heroStatusAtRiskLead", "Atención");
  const heroStatusAccent = isHealthy
    ? t("estadoFiscal.heroStatusOkAccent", "detectados.")
    : effectiveBreakdown.band === "needsAttention"
      ? t("estadoFiscal.heroStatusWarnAccent", "para revisar.")
      : t("estadoFiscal.heroStatusAtRiskAccent", "requerida.");
  const heroDescription = isHealthy ? (
    <>
      {t("estadoFiscal.heroDescOkLead", "Fiscalo revisó tu operación — facturación, conciliación, retenciones, vencimientos. Todo en orden.")}
    </>
  ) : (
    <>
      {t("estadoFiscal.heroDescIssuesLead", "Hay")}{" "}
      <b>
        {issuesCount}{" "}
        {issuesCount === 1
          ? t("estadoFiscal.heroDescIssue", "tema")
          : t("estadoFiscal.heroDescIssues", "temas")}
      </b>{" "}
      {t("estadoFiscal.heroDescIssuesTrailing", "que conviene revisar este mes.")}
    </>
  );
  const facturacionRangeLabel = `${new Intl.DateTimeFormat(i18n.language, { month: "long" }).format(lastMonthDate)} → ${new Intl.DateTimeFormat(i18n.language, { month: "long" }).format(now)}`;
  const primaryAccountant = accountants[0];
  const accountantDisplayName = primaryAccountant
    ? (primaryAccountant.displayName?.trim() ||
        [primaryAccountant.firstName, primaryAccountant.lastName]
          .filter(Boolean)
          .join(" ")
          .trim() ||
        primaryAccountant.email)
    : "";
  const accountantInitials = primaryAccountant
    ? accountantDisplayName
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((p: string) => p[0]?.toUpperCase() ?? "")
        .join("") || (primaryAccountant.email?.[0]?.toUpperCase() ?? "?")
    : "?";

  const monthAbbrevOf = (d: Date): string =>
    new Intl.DateTimeFormat(i18n.language, { month: "short" })
      .format(d)
      .replace(/\./g, "")
      .toUpperCase()
      .slice(0, 3);

  // Build MINI CALENDAR marks for the current month.
  const miniCalendarData = (() => {
    const firstOfMonth = new Date(currentYear, currentMonth, 1);
    const lastOfMonth = new Date(currentYear, currentMonth + 1, 0);
    const daysInMonth = lastOfMonth.getDate();
    const jsDay = firstOfMonth.getDay();
    const firstDayWeekday = (jsDay === 0 ? 7 : jsDay) as 1 | 2 | 3 | 4 | 5 | 6 | 7;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayIsThisMonth =
      today.getFullYear() === currentYear && today.getMonth() === currentMonth;
    const marks: Record<number, MiniCalendarKind> = {};
    for (const o of activeMonthObligations) {
      if (o.status === "completed") continue;
      const due = parseLocalDateOnly(o.dueDate);
      const day = due.getDate();
      const isUrgent =
        o.status === "overdue" ||
        o.status === "due_today" ||
        o.status === "dueSoon";
      const kind: MiniCalendarKind = isUrgent ? "urgent" : "event";
      const existing = marks[day];
      if (existing !== "urgent") marks[day] = kind;
    }
    if (todayIsThisMonth) marks[today.getDate()] = "today";
    const eventCount = Object.keys(marks).filter(
      (k) => marks[Number(k)] !== "today",
    ).length;
    const monthFullFmt = new Intl.DateTimeFormat(i18n.language, { month: "long" });
    const monthLabel = monthFullFmt.format(firstOfMonth);
    const monthLabelCap = monthLabel.charAt(0).toUpperCase() + monthLabel.slice(1);
    return {
      monthLabel: monthLabelCap,
      yearAccent: `${currentYear}.`,
      eventCount,
      firstDayWeekday,
      daysInMonth,
      marks,
    };
  })();

  // Build OBLIGATION list rows for activeMonthObligations.
  // Treat as declared when either status === "completed" or completedAt is set —
  // covers the brief window where one field updates before the other after a mark-paid action.
  const obligationRows = activeMonthObligations.map((o) => {
    const due = parseLocalDateOnly(o.dueDate);
    const day = String(due.getDate()).padStart(2, "0");
    const monthAbbrev = monthAbbrevOf(due);
    const isDeclared = o.status === "completed" || Boolean(o.completedAt);
    let state: ObligationState = "pending";
    if (isDeclared) state = "declared";
    else if (
      o.status === "overdue" ||
      o.status === "due_today" ||
      o.status === "dueSoon"
    )
      state = "urgent";
    return { obligation: o, day, monthAbbrev, state };
  });

  const obligacionesCount = obligationRows.length;
  const urgentCount = obligationRows.filter((r) => r.state === "urgent").length;
  const countLabel =
    obligacionesCount === 0
      ? t("estadoFiscal.obligacionesNone", "Sin obligaciones este mes")
      : urgentCount > 0
        ? t(
            "estadoFiscal.obligacionesCountWithUrgent",
            "{{count}} obligaciones · {{urgent}} urgente",
            { count: obligacionesCount, urgent: urgentCount },
          )
        : t("estadoFiscal.obligacionesCount", "{{count}} obligaciones", {
            count: obligacionesCount,
          });

  return (
    <div className="space-y-4 fx-content">
      <PageHeader
        eyebrow={t("estadoFiscal.eyebrow", "Contabilidad")}
        title={
          <>
            Estado <span className="fx-h1-serif">fiscal.</span>
          </>
        }
        description={t("estadoFiscal.subtitle")}
      />

      <div className="ef-health">
        <SaludFiscalHero
          score={effectiveBreakdown.score}
          indicatorsChecked={effectiveBreakdown.score}
          statusLead={heroStatusLead}
          statusAccent={heroStatusAccent}
          periodLabel={heroPeriodLabel}
          description={heroDescription}
          explainHref={() => setHealthModalOpen(true)}
        />
        <div className="ef-side">
          <FacturacionCard
            neto={neto}
            iva={iva}
            formatCurrency={formatDashboardCurrency}
            rangeLabel={facturacionRangeLabel}
            onViewInvoices={() => navigate("/invoices")}
          />
          {!isAccountant && primaryAccountant ? (
            <ContadorCard
              name={accountantDisplayName}
              email={primaryAccountant.email ?? ""}
              initials={accountantInitials}
              // Pass the member in router state, exactly as
              // EstadoFiscalAccountantsCard and users-page already do.
              // UserDetailPage reads it from location.state; arriving without it
              // used to leave the page on its loading spinner forever.
              onViewProfile={() =>
                navigate(`/users/${primaryAccountant.userId}`, {
                  state: { member: primaryAccountant },
                })
              }
            />
          ) : !isAccountant ? (
            <EstadoFiscalAccountantsCard
              accountants={accountants}
              pendingAccountantInvites={pendingAccountantInvites}
            />
          ) : null}
        </div>
      </div>

      <section>
        <ObligacionesSectionHead
          title={t("estadoFiscal.obligacionesSectionTitle", "Obligaciones de")}
          titleSerif={`${heroPeriodLabel}.`}
          countLabel={countLabel}
        />
        <div className="ef-oblig">
          {obligationRows.length === 0 ? (
            <div className="ef-ob">
              <div className="ef-ob-head" style={{ gridTemplateColumns: "1fr" }}>
                <div className="ob-body">
                  <div className="ob-desc">
                    {t(
                      "estadoFiscal.obligacionesNoneDesc",
                      "No hay obligaciones registradas para este período.",
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : null}
          {obligationRows.map(({ obligation: o, day, monthAbbrev, state }) => {
            const isIva = o.taxType === "IVA";
            const expanded =
              isIva
                ? ivaDetailExpanded
                : agendaSlotMarkCompleteObligation?.id === o.id;
            const onToggle = (): void => {
              if (isIva) setIvaDetailExpanded((v) => !v);
            };
            const taxLabel = t(getTaxLabelKey(o.taxType), o.taxType);
            const ivaRegimeText =
              ivaRegimeShortLabelKey != null ? t(ivaRegimeShortLabelKey) : null;
            // IVA Mínimo replaces the per-period debit-credit calc with a
            // flat tier — surface it as a teal pill so Literal E users
            // immediately understand why the "A pagar" number doesn't
            // match their facturas. CEDE / NO CEDE keep the lighter
            // parenthetical because they're "just" calendar variants.
            const isIvaMinimo = isIva && ivaRegime === "minimum";
            const nameSerif =
              isIva && ivaRegimeText && !isIvaMinimo
                ? `(${ivaRegimeText})`
                : undefined;
            const nameBadge = isIvaMinimo ? (
              <Badge
                variant="default"
                title={t(
                  "estadoFiscal.ivaMinimoBadgeTooltip",
                  "Pagás una cuota fija mensual (con tope reducido los primeros 24 meses) en lugar del IVA débito − crédito.",
                )}
              >
                {t("estadoFiscal.ivaMinimoBadge", "Mínimo")}
              </Badge>
            ) : null;
            const tagText = (() => {
              if (state === "declared")
                return t("estadoFiscal.obligacionTagDeclared", "Pagado");
              if (state === "urgent") {
                if (o.status === "overdue")
                  return t("estadoFiscal.obligacionTagOverdue", "Vencida");
                if (o.status === "due_today")
                  return t("estadoFiscal.obligacionTagDueToday", "Vence hoy");
                return t(
                  "estadoFiscal.obligacionTagDueSoon",
                  "Vence en pocos días",
                );
              }
              return t("estadoFiscal.obligacionTagPending", "Pendiente");
            })();
            const desc = isIva
              ? t(
                  "estadoFiscal.ivaPeriodExplanation",
                  "Calculado con las facturas emitidas en {{month}}.",
                  { month: lastMonthName },
                )
              : t(
                  "estadoFiscal.obligacionDescGeneric",
                  "Vence el {{date}}.",
                  {
                    date: formatDisplayDate(o.dueDate, {
                      useCalendarDate: true,
                      locale: i18n.language,
                    }),
                  },
                );
            const amount = isIva
              ? {
                  label:
                    ivaDgiTotal < 0
                      ? t("estadoFiscal.ivaCreditLabel", "Crédito fiscal")
                      : t("estadoFiscal.ivaAPagar", "A pagar"),
                  value:
                    ivaDgiTotal !== 0
                      ? `${ivaDgiTotal < 0 ? "−" : ""}${formatDashboardCurrency(Math.abs(ivaDgiTotal))} UYU`
                      : "0 UYU",
                }
              : undefined;
            const amountKind: "credit" | "debit" | undefined = isIva
              ? ivaDgiTotal < 0
                ? "credit"
                : "debit"
              : undefined;
            const actionLabel =
              state === "declared"
                ? null
                : t("estadoFiscal.obligacionActionMark", "Marcar como pago");
            const onAction = (): void => {
              if (isIva) {
                setIvaMarkPaidDialogObligation(o);
              } else {
                setAgendaSlotMarkCompleteObligation(o);
              }
            };
            // Ultra-only: when the backend automation has produced a boleto file for an IVA
            // obligation, surface a download action alongside "Marcar como pago". Free/Pro
            // tenants don't get the automation, so they don't see this option.
            const canDownloadBoleto =
              isIva && isUltraTenant && Boolean(o.boletoFileUrl);
            const secondaryActionLabel = canDownloadBoleto
              ? t("estadoFiscal.downloadBoletoDePago", "Descargar boleto de pago")
              : null;
            const onSecondaryAction = (): void => {
              if (o.boletoFileUrl) {
                window.open(o.boletoFileUrl, "_blank", "noopener,noreferrer");
              }
            };
            return (
              <ObligationCard
                key={o.id}
                state={state}
                dateDay={day}
                dateMonth={monthAbbrev}
                name={taxLabel}
                nameSerif={nameSerif}
                nameBadge={nameBadge}
                tag={tagText}
                description={desc}
                amount={amount}
                amountKind={amountKind}
                actionLabel={actionLabel}
                onAction={onAction}
                actionDisabled={
                  markIvaPaid.isPending || markAgendaSlotComplete.isPending
                }
                secondaryActionLabel={secondaryActionLabel}
                onSecondaryAction={onSecondaryAction}
                expanded={Boolean(expanded)}
                onToggle={onToggle}
              >
                {isIva ? (
                  <>
                    <div className="detail-head">
                      <div className="detail-title">
                        {t("estadoFiscal.ivaBalanceTitle", "Balance IVA del")}{" "}
                        <span className="serif">
                          {t("estadoFiscal.ivaBalanceTitleAccent", "período.")}
                        </span>
                      </div>
                      <div className="detail-meta flex items-center gap-2">
                        <span>
                          {t("estadoFiscal.ivaBalancePeriod", "Período")} ·{" "}
                          {formatDisplayDate(prevMonthStart, {
                            useCalendarDate: true,
                            locale: i18n.language,
                          })}{" "}
                          →{" "}
                          {formatDisplayDate(prevMonthEnd, {
                            useCalendarDate: true,
                            locale: i18n.language,
                          })}
                        </span>
                        {isUltraTenant && (
                          <AutoBoletoStatusChip status={autoBoletoStatuses?.[o.id]} />
                        )}
                      </div>
                    </div>
                    <BalanceEquation
                      ventas={{
                        label: t(
                          "estadoFiscal.ivaVentas",
                          "IVA ventas (emitidas)",
                        ),
                        value: formatDashboardCurrency(ivaDebited),
                      }}
                      compras={{
                        label: t(
                          "estadoFiscal.ivaCompras",
                          "IVA compras (recibidas)",
                        ),
                        value: formatDashboardCurrency(ivaCredited),
                      }}
                      periodo={{
                        label: t(
                          "estadoFiscal.ivaAPagarPeriodo",
                          "IVA a pagar (período)",
                        ),
                        value:
                          ivaEstimado !== 0
                            ? `${ivaEstimado < 0 ? "−" : ""}${formatDashboardCurrency(Math.abs(ivaEstimado))}`
                            : "0",
                        credit: ivaEstimado < 0,
                      }}
                      total={{
                        label: t("estadoFiscal.ivaDgiTotal", "Total DGI"),
                        value:
                          ivaDgiTotal !== 0
                            ? `${ivaDgiTotal < 0 ? "−" : ""}${formatDashboardCurrency(Math.abs(ivaDgiTotal))}`
                            : "0",
                        credit: ivaDgiTotal < 0,
                      }}
                    />
                    {showSaldoAFavor ? (
                      <div className="mt-3 rounded-md bg-[var(--fx-accent-100)] px-3 py-2.5 text-sm">
                        <div className="flex flex-wrap items-baseline justify-between gap-2">
                          <span className="font-medium text-[var(--fx-accent-700)]">
                            {t(
                              "estadoFiscal.ivaSaldoAFavorPrior",
                              "Saldo a favor de meses anteriores",
                            )}
                          </span>
                          <span className="text-base font-semibold tabular-nums text-[var(--fx-accent-700)]">
                            + {formatDashboardCurrency(ivaCreditBalance)} UYU
                          </span>
                        </div>
                        <div className="mt-1 text-xs text-[var(--fx-accent-700)]/80">
                          {t(
                            "estadoFiscal.ivaSaldoAFavorExplain",
                            "Se descuenta del IVA estimado del próximo vencimiento.",
                          )}
                        </div>
                      </div>
                    ) : null}
                    <ObligationDetailFoot
                      note={
                        <>
                          {ivaDgiTotal < 0
                            ? t(
                                "estadoFiscal.ivaBalanceNoteCredit",
                                "Este monto es lo que corresponde declarar este mes según las facturas del período indicado. El resultado es un",
                              )
                            : t(
                                "estadoFiscal.ivaBalanceNoteDebit",
                                "Este monto es lo que corresponde declarar y pagar este mes según las facturas del período indicado. El total a abonar es",
                              )}{" "}
                          <b>
                            {ivaDgiTotal < 0
                              ? t(
                                  "estadoFiscal.ivaBalanceCredit",
                                  "crédito a tu favor",
                                )
                              : `${formatDashboardCurrency(ivaDgiTotal)} UYU`}
                          </b>
                          {ivaDgiTotal < 0
                            ? t(
                                "estadoFiscal.ivaBalanceNoteCreditTail",
                                ": no hay que abonar.",
                              )
                            : "."}
                        </>
                      }
                    />

                    <div className="mt-5 space-y-5">
                      <div>
                        <div className="mb-2 text-sm font-semibold text-foreground">
                          {t("estadoFiscal.ivaDetailEmitidas")}
                        </div>
                        {emitidasLoading ? (
                          <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            {t("estadoFiscal.ivaDetailLoading")}
                          </div>
                        ) : (
                          renderIvaVentasEmitidasTable(emitidasData?.data ?? [])
                        )}
                      </div>
                      <div>
                        <div className="mb-2 text-sm font-semibold text-foreground">
                          {t("estadoFiscal.ivaDetailRecibidas")}
                        </div>
                        {recibidasLoading ? (
                          <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            {t("estadoFiscal.ivaDetailLoading")}
                          </div>
                        ) : (
                          renderIvaComprasRecibidasTable(
                            recibidasData?.data ?? [],
                          )
                        )}
                      </div>
                    </div>
                  </>
                ) : null}
              </ObligationCard>
            );
          })}
        </div>
      </section>

      <div className="ef-bottom">
        <Proximos60Dias
          title={t("estadoFiscal.timelineTitle", "Próximos 60 días")}
          events={timelineEvents}
          ctaLabel={t("estadoFiscal.timelineCta", "Ver agenda completa")}
          onCta={() => navigate("/agenda")}
        />
        <MiniCalendar
          monthLabel={miniCalendarData.monthLabel}
          yearAccent={miniCalendarData.yearAccent}
          eventCount={miniCalendarData.eventCount}
          firstDayWeekday={miniCalendarData.firstDayWeekday}
          daysInMonth={miniCalendarData.daysInMonth}
          marks={miniCalendarData.marks}
        />
      </div>

      <div className="mt-8 space-y-4">
        <h2 className="text-base font-semibold tracking-tight text-foreground">
          {t("estadoFiscal.ivaHistoryTitle")}
        </h2>
        <TableCard>
          {ivaHistoryLoading ? (
            <div className="flex min-h-[12rem] items-center justify-center py-6 md:px-6">
              <LoadingLogo size="lg" />
            </div>
          ) : ivaHistoryCurrentYearOnly.length === 0 ? (
            <div className="flex min-h-[12rem] items-center justify-center py-6 md:px-6">
              <EmptyState
                title={t("estadoFiscal.ivaHistoryEmpty")}
                className="py-0"
              />
            </div>
          ) : (
            <>
              <div className="flex flex-col gap-3 pb-4 pt-2 md:hidden">
                {ivaHistoryCurrentYearOnly.map((row) => {
                  const ivaAPagarRounded = roundDgi(row.ivaAPagarUyu);
                  const ivaPagarLabel =
                    ivaAPagarRounded > 0
                      ? `${formatDashboardCurrency(ivaAPagarRounded)} UYU`
                      : ivaAPagarRounded < 0
                        ? `− ${formatDashboardCurrency(-ivaAPagarRounded)} UYU`
                        : "0 UYU";
                  return (
                    <Card
                      key={row.period}
                      className="border-border bg-card shadow-sm transition-shadow duration-150"
                    >
                      <CardContent className="flex flex-col gap-3 p-4">
                        <p className="text-sm font-semibold text-foreground">
                          {formatPeriodForDisplay(row.period, i18n.language)}
                        </p>
                        <dl className="grid gap-2.5 text-sm">
                          <div className="flex justify-between gap-3">
                            <dt className="text-muted-foreground">
                              {t("estadoFiscal.ivaVentas")}
                            </dt>
                            <dd className="text-right tabular-nums font-medium text-foreground">
                              {formatDashboardCurrency(row.ivaDebitedUyu)} UYU
                            </dd>
                          </div>
                          <div className="flex justify-between gap-3">
                            <dt className="text-muted-foreground">
                              {t("estadoFiscal.ivaCompras")}
                            </dt>
                            <dd className="text-right tabular-nums font-medium text-foreground">
                              {formatDashboardCurrency(row.ivaCreditedUyu)} UYU
                            </dd>
                          </div>
                          <div className="flex justify-between gap-3 border-t border-border/60 pt-2.5">
                            <dt className="font-medium text-foreground">
                              {t("estadoFiscal.ivaAPagar")}
                            </dt>
                            <dd className="text-right tabular-nums font-semibold text-foreground">
                              {ivaPagarLabel}
                            </dd>
                          </div>
                        </dl>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
              <div className="hidden md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("estadoFiscal.paymentHistoryPeriod")}</TableHead>
                      <TableHead>{t("estadoFiscal.ivaVentas")}</TableHead>
                      <TableHead>{t("estadoFiscal.ivaCompras")}</TableHead>
                      <TableHead>{t("estadoFiscal.ivaAPagar")}</TableHead>
                      <TableHead>{t("estadoFiscal.ivaPagadoColumn")}</TableHead>
                      <TableHead>{t("estadoFiscal.ivaSaldoColumn")}</TableHead>
                      <TableHead className="w-10" aria-hidden />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ivaHistoryCurrentYearOnly.map((row) => {
                      const ivaAPagarRounded = roundDgi(row.ivaAPagarUyu);
                      const paid = row.ivaActuallyPaidUyu;
                      const balance = row.ivaBalanceUyu;
                      return (
                        <TableRow key={row.period}>
                          <TableCell className="font-medium">
                            {formatPeriodForDisplay(row.period, i18n.language)}
                          </TableCell>
                          <TableCell className="tabular-nums">
                            {formatDashboardCurrency(row.ivaDebitedUyu)} UYU
                          </TableCell>
                          <TableCell className="tabular-nums">
                            {formatDashboardCurrency(row.ivaCreditedUyu)} UYU
                          </TableCell>
                          <TableCell className="tabular-nums">
                            {ivaAPagarRounded > 0
                              ? `${formatDashboardCurrency(ivaAPagarRounded)} UYU`
                              : ivaAPagarRounded < 0
                                ? `− ${formatDashboardCurrency(-ivaAPagarRounded)} UYU`
                                : "0 UYU"}
                          </TableCell>
                          <TableCell className="tabular-nums text-muted-foreground">
                            {paid != null
                              ? `${formatDashboardCurrency(paid)} UYU`
                              : "—"}
                          </TableCell>
                          <TableCell
                            className={cn(
                              "tabular-nums",
                              balance != null && balance > 0 && "text-green-700 dark:text-green-400",
                              balance != null && balance < 0 && "text-red-700 dark:text-red-400",
                            )}
                          >
                            {balance == null
                              ? "—"
                              : balance > 0
                                ? `+ ${formatDashboardCurrency(balance)} UYU`
                                : balance < 0
                                  ? `− ${formatDashboardCurrency(-balance)} UYU`
                                  : "0 UYU"}
                          </TableCell>
                          <TableCell>
                            {row.obligationId ? (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setRecordIvaPaymentRow({
                                    period: row.period,
                                    obligationId: row.obligationId!,
                                    currentAmount: paid,
                                  });
                                  setRecordIvaPaymentInput(
                                    paid != null ? String(paid) : "",
                                  );
                                }}
                              >
                                {paid != null
                                  ? t("estadoFiscal.ivaEditPaymentAction")
                                  : t("estadoFiscal.ivaRecordPaymentAction")}
                              </Button>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </TableCard>
      </div>

      <FiscalHealthExplanationModal
        open={healthModalOpen}
        onOpenChange={setHealthModalOpen}
        breakdown={effectiveBreakdown}
        overdueObligations={overdueObligations}
        dueTodayObligations={dueTodayObligations}
        dueIn1Obligations={dueIn1Obligations}
        dueIn3Obligations={dueIn3Obligations}
      />

      <MarkFiscalCompleteDialog
        obligation={ivaMarkPaidDialogObligation}
        onClose={() => setIvaMarkPaidDialogObligation(null)}
        defaultAmountUyu={
          ivaMarkPaidDialogObligation
            ? Math.max(0, ivaDgiTotalForHook)
            : undefined
        }
        onConfirm={(completedDate, notes, amountPaidUyu) => {
          if (!ivaMarkPaidDialogObligation) return;
          markIvaPaid.mutate({
            obligationId: ivaMarkPaidDialogObligation.id,
            completedDate,
            notes: notes || undefined,
            amountPaidUyu,
          });
        }}
        isSubmitting={markIvaPaid.isPending}
      />

      <MarkFiscalCompleteDialog
        obligation={agendaSlotMarkCompleteObligation}
        onClose={() => setAgendaSlotMarkCompleteObligation(null)}
        onConfirm={(completedDate, notes) => {
          if (!agendaSlotMarkCompleteObligation) return;
          markAgendaSlotComplete.mutate({
            obligationId: agendaSlotMarkCompleteObligation.id,
            completedDate,
            notes: notes || undefined,
          });
        }}
        isSubmitting={markAgendaSlotComplete.isPending}
      />

      <Dialog
        open={!!recordIvaPaymentRow}
        onOpenChange={(open) => {
          if (!open) {
            setRecordIvaPaymentRow(null);
            setRecordIvaPaymentInput("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t("estadoFiscal.ivaRecordPaymentDialogTitle", "Registrar pago de IVA")}
            </DialogTitle>
            <DialogDescription>
              {recordIvaPaymentRow
                ? t("estadoFiscal.ivaRecordPaymentDialogDescription", {
                    defaultValue:
                      "Ingresá el monto que pagaste a DGI por el período {{period}}.",
                    period: formatPeriodForDisplay(
                      recordIvaPaymentRow.period,
                      i18n.language,
                    ),
                  })
                : null}
            </DialogDescription>
          </DialogHeader>
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (!recordIvaPaymentRow) return;
              const trimmed = recordIvaPaymentInput.trim();
              if (trimmed === "") {
                recordIvaPayment.mutate({
                  obligationId: recordIvaPaymentRow.obligationId,
                  amountPaidUyu: null,
                });
                return;
              }
              const parsed = Number(trimmed.replace(",", "."));
              if (!Number.isFinite(parsed) || parsed < 0) {
                toast.error(
                  t(
                    "estadoFiscal.ivaRecordPaymentInvalid",
                    "Monto inválido. Ingresá un número mayor o igual a 0.",
                  ),
                );
                return;
              }
              recordIvaPayment.mutate({
                obligationId: recordIvaPaymentRow.obligationId,
                amountPaidUyu: parsed,
              });
            }}
          >
            <div className="space-y-2">
              <Label htmlFor="iva-amount-paid">
                {t("estadoFiscal.ivaRecordPaymentAmountLabel", "Monto pagado (UYU)")}
              </Label>
              <Input
                id="iva-amount-paid"
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                value={recordIvaPaymentInput}
                onChange={(e) => setRecordIvaPaymentInput(e.target.value)}
                placeholder="0.00"
                autoFocus
              />
              <p className="text-xs text-muted-foreground">
                {t(
                  "estadoFiscal.ivaRecordPaymentHint",
                  "Dejá el campo vacío y guardá para borrar el monto registrado.",
                )}
              </p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setRecordIvaPaymentRow(null);
                  setRecordIvaPaymentInput("");
                }}
                disabled={recordIvaPayment.isPending}
              >
                {t("fiscalAgenda.dialogCancel")}
              </Button>
              <Button type="submit" disabled={recordIvaPayment.isPending}>
                {recordIvaPayment.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  t("estadoFiscal.ivaRecordPaymentSubmit", "Guardar")
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={ivaInstructionsOpen} onOpenChange={setIvaInstructionsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {ivaObligationForPeriod?.boletoFileUrl || ivaObligationForPeriod?.boletoNumber
                ? t("estadoFiscal.ivaPayInstructionsTitle")
                : t("estadoFiscal.ivaInstructionsTitle")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-muted-foreground">
            {ivaObligationForPeriod?.boletoFileUrl || ivaObligationForPeriod?.boletoNumber ? (
              <>
                <div className="rounded-md border border-border bg-muted/30 p-4 space-y-2">
                  <div>
                    <p className="text-xs font-medium text-muted-foreground">
                      {t("estadoFiscal.ivaPayInstructionsRutLabel")}
                    </p>
                    <p className="text-sm font-semibold tabular-nums text-foreground">
                      {currentTenant?.rut ?? "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground">
                      {t("estadoFiscal.ivaPayInstructionsRefLabel")}
                    </p>
                    <p className="text-sm font-semibold tabular-nums text-foreground">
                      {ivaObligationForPeriod?.boletoNumber ?? "—"}
                    </p>
                  </div>
                </div>
                <ol className="list-decimal list-inside space-y-2">
                  <li>{t("estadoFiscal.ivaPayInstructionsStep1")}</li>
                  <li>{t("estadoFiscal.ivaPayInstructionsStep2")}</li>
                  <li>{t("estadoFiscal.ivaPayInstructionsStep3")}</li>
                  <li>{t("estadoFiscal.ivaPayInstructionsStep4")}</li>
                  <li>{t("estadoFiscal.ivaPayInstructionsStep5")}</li>
                  <li>{t("estadoFiscal.ivaPayInstructionsStep6")}</li>
                </ol>
                <p className="pt-2">
                  <a
                    href={DGI_2908_PAY_BY_RUT_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary underline hover:no-underline"
                  >
                    {t("estadoFiscal.ivaGoToDgiPayByRut")}
                  </a>
                </p>
              </>
            ) : (
              <>
                {ivaRegimeFullLabelKey != null && (
                  <p>
                    {t("estadoFiscal.ivaInstructionsRegimeNote", {
                      regimeLabel: t(ivaRegimeFullLabelKey),
                    })}
                  </p>
                )}
                <ol className="list-decimal list-inside space-y-2">
                  <li>{t("estadoFiscal.ivaInstructionsStep1")}</li>
                  <li>{t("estadoFiscal.ivaInstructionsStep2")}</li>
                  <li>{t("estadoFiscal.ivaInstructionsStep3")}</li>
                  <li>{t("estadoFiscal.ivaInstructionsStep4")}</li>
                  <li>{t("estadoFiscal.ivaInstructionsStep5")}</li>
                </ol>
                <p className="pt-2">
                  <a
                    href={DGI_2908_PAYMENT_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary underline hover:no-underline"
                  >
                    {t("estadoFiscal.ivaGoToDgiPay")}
                  </a>
                </p>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!payBoletoModalObligation} onOpenChange={(open) => !open && setPayBoletoModalObligation(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("estadoFiscal.payBoletoModalTitle")}</DialogTitle>
          </DialogHeader>
          {payBoletoModalObligation && (
            <div className="space-y-4">
              <div className="rounded-md border bg-muted/30 p-4 space-y-2">
                <div>
                  <p className="text-xs font-medium text-muted-foreground">{t("estadoFiscal.payBoletoModalRut")}</p>
                  <p className="text-sm font-semibold tabular-nums text-foreground">{currentTenant?.rut ?? "—"}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-muted-foreground">{t("estadoFiscal.payBoletoModalBoletoNumber")}</p>
                  <p className="text-sm font-semibold tabular-nums text-foreground">{payBoletoModalObligation.boletoNumber ?? "—"}</p>
                </div>
              </div>
              <Button
                type="button"
                className="w-full"
                onClick={async () => {
                  const rut = currentTenant?.rut?.trim();
                  if (rut) {
                    try {
                      await navigator.clipboard.writeText(rut);
                      toast.success(t("estadoFiscal.rucCopiedToClipboard"));
                    } catch {
                      toast.error(t("estadoFiscal.rucCopyFailed"));
                    }
                  }
                  window.open(DGI_2908_PAY_BY_RUT_URL, "_blank", "noopener,noreferrer");
                }}
              >
                {t("estadoFiscal.payBoletoModalOpenDgi")}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={uploadBoletoDialogOpen} onOpenChange={setUploadBoletoDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("estadoFiscal.uploadBoletoDialogTitle")}</DialogTitle>
          </DialogHeader>
          {ivaObligationForPeriod && (
            <UploadBoletoForm
              obligationId={ivaObligationForPeriod.id}
              onSuccess={() => {
                setUploadBoletoDialogOpen(false);
                queryClient.invalidateQueries({ queryKey: ["fiscal-agenda"] });
              }}
              onCancel={() => setUploadBoletoDialogOpen(false)}
              uploadBoletoMutation={uploadBoletoMutation}
            />
          )}
        </DialogContent>
      </Dialog>

      <ConfirmationDialog
        open={removeBoletoObligation !== null}
        onOpenChange={(open) => !open && setRemoveBoletoObligation(null)}
        title={t("estadoFiscal.ivaRemoveBoletoConfirmTitle")}
        description={t("estadoFiscal.ivaRemoveBoletoConfirmDescription")}
        confirmText={t("estadoFiscal.ivaRemoveBoleto")}
        variant="destructive"
        onConfirm={() => {
          if (removeBoletoObligation) removeBoletoMutation.mutate(removeBoletoObligation.id);
        }}
        isLoading={removeBoletoMutation.isPending}
      />
    </div>
  );
}
